import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BasicRecord, SCAdvanceTableProps } from './types';

interface PortalMenuItem {
  Label?: string;
  Icon?: IconProp;
  OnClick?: (id: string, row: any) => void;
  Disabled?: boolean;
  Divider?: boolean;
  Visible?: boolean;
}

interface PortalState {
  items: PortalMenuItem[];
  rect: DOMRect;
  rowId: string;
  row: any;
}

/**
 * Workaround for eone-ui's TableRowButton not supporting MenuItems dropdowns.
 *
 * Strips MenuItems from RowButtons (so eone-ui renders a plain button),
 * and replaces OnClick with a handler that opens a portal-based dropdown
 * rendered using eone-ui's own CSS classes so it looks identical.
 */
function useMenuButtonPortal<D extends BasicRecord>(
  rowButtons: SCAdvanceTableProps<D>['RowButtons'],
): {
  strippedButtons: SCAdvanceTableProps<D>['RowButtons'];
  portalElement: React.ReactNode;
} {
  const [portalState, setPortalState] = useState<PortalState | null>(null);

  const close = useCallback(() => setPortalState(null), []);

  // Close on any outside mousedown
  useEffect(() => {
    if (!portalState) return;
    const handler = () => close();
    // Defer by one tick so the opening click doesn't immediately close it
    const timer = setTimeout(() => window.addEventListener('mousedown', handler), 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handler);
    };
  }, [portalState, close]);

  // Strip MenuItems and replace OnClick with a portal opener
  const strippedButtons = useMemo(() => {
    return rowButtons?.map((button) => {
      if (!button.MenuItems?.length) return button;

      const menuItems = button.MenuItems;
      return {
        ...button,
        MenuItems: undefined,
        OnClick: (idField: string, row: D) => {
          const anchor = document.activeElement?.getBoundingClientRect();
          if (!anchor) return;
          setPortalState({ items: menuItems, rect: anchor, rowId: idField, row });
        },
      };
    });
  }, [rowButtons]) as SCAdvanceTableProps<D>['RowButtons'];

  const portalElement = portalState
    ? createPortal(
        <div
          style={{
            position: 'fixed',
            top: portalState.rect.bottom + 4,
            left: portalState.rect.right,
            transform: 'translateX(-100%)',
            zIndex: 9999,
            width: 'max-content',
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Reuse eone-ui's dropdown CSS classes for identical appearance.
              Must be a div (not span) — the CSS sets background/border/shadow
              which require block-level display to render correctly. */}
          <div className="eone-ui-button-drop-down-menu">
            <div className="eone-ui-button-drop-down-menu-container">
              {portalState.items
                .filter((item) => item.Visible !== false)
                .map((item, i) => {
                  if (item.Divider) {
                    return <div key={i} className="eone-ui-button-drop-down-menu-divider" />;
                  }

                  const handleClick = () => {
                    close();
                    item.OnClick?.(portalState.rowId, portalState.row);
                  };

                  if (item.Disabled) {
                    return (
                      <button
                        key={i}
                        type="button"
                        className="eone-ui-button-drop-down-menu-item-disabled"
                        disabled
                      >
                        {item.Icon && (
                          <div className="eone-ui-button-drop-down-menu-item-icon">
                            <FontAwesomeIcon icon={item.Icon as IconProp} fixedWidth />
                          </div>
                        )}
                        <div className="eone-ui-button-drop-down-menu-item-label">{item.Label}</div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={i}
                      type="button"
                      className="eone-ui-button-drop-down-menu-item"
                      onClick={handleClick}
                    >
                      {item.Icon && (
                        <div className="eone-ui-button-drop-down-menu-item-icon">
                          <FontAwesomeIcon icon={item.Icon as IconProp} fixedWidth />
                        </div>
                      )}
                      <div className="eone-ui-button-drop-down-menu-item-label">{item.Label}</div>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return { strippedButtons, portalElement };
}

export { useMenuButtonPortal };
